import React from 'react'
import {ColorPath} from 'types/Theme'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {RefundType} from '@vio/core/src/offer/business/cancellationPenalties'

import UnifiedDateTime from '../../utils/UnifiedDateTime'
import {TextAlignedIcon} from './styles'

interface CancellationLabelPropsType {
  /** Datetime of the cancellation deadline */
  cancellationDeadline: string
  /** Timezone to display free cancellation date */
  timezone?: string
  /** Type of cancellation penalty (e.g., non-refundable, partial, free cancellation) */
  refundType: RefundType
  /** Applies sold-out styling to visually indicate that the offer is no longer available. */
  isOfferSoldOut?: boolean
  /** Renders the cancellation label without the deadline date (free & partial refund) */
  hideDeadline?: boolean
}

const getColorPath = ({
  refundType,
  isOfferSoldOut
}: {
  refundType: RefundType
  isOfferSoldOut: boolean
}): ColorPath => {
  if (isOfferSoldOut) {
    return 'content.neutral.disabled'
  } else if (refundType === RefundType.freeCancellation) {
    return 'content.success.secondary'
  }

  return 'content.neutral.primary'
}

const getCancellationIcon = (
  refundType: RefundType,
  isOfferSoldOut: boolean
) => {
  const colorPath = getColorPath({refundType, isOfferSoldOut})

  const iconName =
    refundType === RefundType.nonRefundable
      ? 'CalendarCross3'
      : 'CalendarCheck4'

  return <TextAlignedIcon size="sm" name={iconName} colorPath={colorPath} />
}

const getCancellationMessage = (
  refundType: RefundType,
  cancellationDeadline = '',
  isOfferSoldOut: boolean,
  timezone?: string,
  hideDeadline = false
) => {
  if (refundType === RefundType.freeCancellation) {
    if (hideDeadline) {
      return (
        <span data-id="cancellationType.refundable">
          <FormattedMessageWrapper
            id="cancellationPenalties.freeCancellation"
            defaultMessage="Free cancellation"
          />
        </span>
      )
    }

    return (
      <span data-id="cancellationType.refundable">
        <FormattedMessageWrapper
          id="freeCancellationDeadline"
          defaultMessage="Free cancellation before {date}"
          values={{
            date: (
              <UnifiedDateTime
                value={cancellationDeadline}
                format="date-short"
                timeZone={timezone}
              />
            )
          }}
        />
      </span>
    )
  }

  if (refundType === RefundType.nonRefundable) {
    return (
      <span data-id="cancellationType.nonRefundable">
        <FormattedMessageWrapper
          id="cancellationPenalties.nonRefundable"
          defaultMessage="Non-refundable"
        />
      </span>
    )
  }

  if (refundType === RefundType.partialRefund) {
    if (hideDeadline) {
      return (
        <span data-id="cancellationType.partial">
          <FormattedMessageWrapper
            id="cancellationPenalties.partiallyRefundable"
            defaultMessage="Partially refundable"
          />
        </span>
      )
    }

    return (
      <span data-id="cancellationType.partial">
        <FormattedMessageWrapper
          id="partialRefundDeadlineV2"
          defaultMessage="Partially refundable before {date}"
          values={{
            date: (
              <UnifiedDateTime
                value={cancellationDeadline}
                format="date-short"
                timeZone={timezone}
              />
            )
          }}
        />
      </span>
    )
  }

  return null
}

export const CancellationLabel = ({
  cancellationDeadline,
  refundType,
  timezone,
  isOfferSoldOut = false,
  hideDeadline = false
}: CancellationLabelPropsType) => {
  return (
    <Box
      display="flex"
      alignItems="baseline"
      gap="s250"
      data-id="cancellation-label"
    >
      {getCancellationIcon(refundType, isOfferSoldOut)}
      <Text
        variant={
          refundType === RefundType.freeCancellation ? 'labelS' : 'bodyS'
        }
        colorPath={getColorPath({refundType, isOfferSoldOut})}
      >
        {getCancellationMessage(
          refundType,
          cancellationDeadline,
          isOfferSoldOut,
          timezone,
          hideDeadline
        )}
      </Text>
    </Box>
  )
}
